//src/contexts/AuthContext.js - Complete with localStorage support
import React, { createContext, useContext, useState, useEffect ,useCallback} from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is already authenticated on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Check if we have a valid token in localStorage
        if (apiService.isAuthenticated()) {
          
          // Get fresh user data from the server
          const userData = await apiService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
          
        } else {
          console.log('No valid token found');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // If the token is invalid or expired, remove it
        apiService.removeAuthToken();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function - called after successful authentication
  const login = useCallback(async (userData, token) => {
    try {
      // Token is already saved by apiService.authenticateWithMagicLink
      // Just update the context state
      setUser(userData);
      setIsAuthenticated(true);
      
      console.log('User logged in:', userData.email);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  });

  // Logout function
  const logout = () => {
    try {
      // Clear token from localStorage
      apiService.removeAuthToken();
      
      // Clear context state
      setUser(null);
      setIsAuthenticated(false);
      
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Send magic link to email
  const sendMagicLink = async (email) => {
    try {
      const response = await apiService.sendMagicLink(email);
      return response;
    } catch (error) {
      console.error('Send magic link error:', error);
      throw error;
    }
  };

  // Authenticate with magic link token
  const authenticateWithMagicLink = useCallback(async (magicToken) => {
  try {
    const response = await apiService.authenticateWithMagicLink(magicToken);

    // Login the user with the response data
    await login(response.user, response.access_token);

    return response;
  } catch (error) {
    console.error('Magic link authentication error:', error);
    throw error;
  }
}, [login]);

  // Refresh user data (useful for updating user info)
  const refreshUser = async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }
      
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      
      console.log('User data refreshed');
      return userData;
    } catch (error) {
      console.error('Refresh user error:', error);
      
      // If token is invalid, log out the user
      if (error.message.includes('session') || error.message.includes('expired')) {
        logout();
      }
      
      throw error;
    }
  };

  // Get user info from token without API call (useful for quick checks)
  const getUserFromToken = () => {
    return apiService.getUserInfoFromToken();
  };

  // Check if current user has specific role
  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  const value = {
    // State
    user,
    isAuthenticated,
    loading,
    
    // Authentication methods
    login,
    logout,
    sendMagicLink,
    authenticateWithMagicLink,
    
    // Utility methods
    refreshUser,
    getUserFromToken,
    hasRole,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;